// src/components/AdminDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  getVideoTitle,
  isAvailableToClaim,
  isInReviewVideo,
  isReviewedVideo,
  isUploadComplete,
} from '../utils/videoWorkflow';
import { getCanonicalOrgId } from '../utils/drillTemplates';
import { getPlatformMetrics } from '../utils/platformMetrics';
import { getPlatformAnalytics } from '../utils/analytics';

function AdminDashboard() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [videos, setVideos] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [organizationInvites, setOrganizationInvites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contentPacks, setContentPacks] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState({});
  const [orgDrafts, setOrgDrafts] = useState({});
  const [orgFilter, setOrgFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const usersRef = collection(db, 'users');
        const videosRef = collection(db, 'videos');
        const [usersSnapshot, videosSnapshot, lessonsSnapshot, organizationsSnapshot, organizationMembersSnapshot, organizationInvitesSnapshot, templatesSnapshot, contentPacksSnapshot, entitlementsSnapshot, subscriptionsSnapshot, subscriptionPlansSnapshot] = await Promise.all([
          getDocs(usersRef),
          getDocs(videosRef),
          getDocs(collection(db, 'lessonInstances')),
          getDocs(collection(db, 'organizations')),
          getDocs(collection(db, 'organizationMembers')),
          getDocs(collection(db, 'organizationInvites')),
          getDocs(collection(db, 'drillTemplates')),
          getDocs(collection(db, 'contentPacks')),
          getDocs(collection(db, 'entitlements')),
          getDocs(collection(db, 'organizationSubscriptions')),
          getDocs(collection(db, 'subscriptionPlans')),
        ]);

        const users = usersSnapshot.docs.map((userDoc) => ({
          id: userDoc.id,
          ...userDoc.data(),
          orgId: getCanonicalOrgId(userDoc.data()),
        }));

        const pendingApps = users.filter(
          (user) => user.applicationStatus === 'pending' && user.instructorApplication
        );

        const availableInstructors = users.filter((user) => user.role === 'instructor');

        const videoList = videosSnapshot.docs
          .map((videoDoc) => ({
            id: videoDoc.id,
            ...videoDoc.data(),
          }))
          .filter((video) => video.coachType === 'real')
          .filter(isUploadComplete);

        setApplications(pendingApps);
        setInstructors(availableInstructors);
        setVideos(videoList);
        setLessons(lessonsSnapshot.docs.map((docSnap) => ({ lessonId: docSnap.id, ...docSnap.data() })));
        setOrganizations(organizationsSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setOrganizationMembers(organizationMembersSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setOrganizationInvites(organizationInvitesSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setTemplates(templatesSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setContentPacks(contentPacksSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setEntitlements(entitlementsSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setSubscriptions(subscriptionsSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setSubscriptionPlans(subscriptionPlansSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setOrgDrafts(
          users.reduce((acc, user) => {
            acc[user.id] = {
              orgId: user.orgId || '',
              orgName: user.orgName || '',
              orgRole: user.orgRole || (user.role === 'admin' ? 'admin' : 'instructor'),
            };
            return acc;
          }, {})
        );
        setSelectedAssignments(
          videoList.reduce((acc, video) => {
            acc[video.id] = video.assignedTo || '';
            return acc;
          }, {})
        );
      } catch (error) {
        console.error('Error fetching admin dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const pendingAssignmentVideos = useMemo(
    () => videos.filter(isAvailableToClaim),
    [videos]
  );

  const assignedVideos = useMemo(
    () => videos.filter(isInReviewVideo),
    [videos]
  );

  const reviewedVideos = useMemo(
    () => videos.filter(isReviewedVideo),
    [videos]
  );

  const instructorWorkload = useMemo(
    () =>
      instructors.map((instructor) => {
        const assignedCount = videos.filter(
          (video) => isInReviewVideo(video) && video.assignedTo === instructor.id
        ).length;
        const reviewedCount = videos.filter(
          (video) => isReviewedVideo(video) && video.reviewedBy === instructor.id
        ).length;

        return {
          ...instructor,
          assignedCount,
          reviewedCount,
        };
      }),
    [instructors, videos]
  );

  const orgBuckets = useMemo(() => {
    const buckets = instructorWorkload.reduce((acc, instructor) => {
      const orgId = instructor.orgId || '__unassigned__';
      const orgName = instructor.orgName || (orgId === '__unassigned__' ? 'Unassigned' : orgId);

      if (!acc[orgId]) {
        acc[orgId] = {
          orgId,
          orgName,
          instructorCount: 0,
          assignedCount: 0,
          reviewedCount: 0,
          instructors: [],
        };
      }

      acc[orgId].instructorCount += 1;
      acc[orgId].assignedCount += instructor.assignedCount;
      acc[orgId].reviewedCount += instructor.reviewedCount;
      acc[orgId].instructors.push(instructor);

      return acc;
    }, {});

    return Object.values(buckets).sort((a, b) => {
      if (a.orgId === '__unassigned__') return 1;
      if (b.orgId === '__unassigned__') return -1;
      return a.orgName.localeCompare(b.orgName);
    });
  }, [instructorWorkload]);

  const filteredOrgBuckets = useMemo(() => {
    if (orgFilter === 'all') {
      return orgBuckets;
    }

    return orgBuckets.filter((bucket) => bucket.orgId === orgFilter);
  }, [orgBuckets, orgFilter]);
  const platformMetrics = useMemo(
    () =>
      getPlatformMetrics({
        organizations,
        members: instructors,
        lessons,
        videos,
        templates,
        contentPacks,
        entitlements,
      }),
    [contentPacks, entitlements, instructors, lessons, organizations, templates, videos]
  );
  const platformAnalytics = useMemo(
    () =>
      getPlatformAnalytics({
        organizations,
        organizationMembers,
        invites: organizationInvites,
        lessons,
        videos,
        templates,
        contentPacks,
        entitlements,
        subscriptions,
        plans: subscriptionPlans,
      }),
    [
      contentPacks,
      entitlements,
      lessons,
      organizationInvites,
      organizationMembers,
      organizations,
      subscriptionPlans,
      subscriptions,
      templates,
      videos,
    ]
  );

  const setFlashMessage = (message) => {
    setActionMessage(message);
    window.clearTimeout(setFlashMessage.timeoutId);
    setFlashMessage.timeoutId = window.setTimeout(() => {
      setActionMessage('');
    }, 2500);
  };

  const handleDecision = async (userId, decision) => {
    try {
      const userRef = doc(db, 'users', userId);
      const updateData = {
        applicationStatus: decision,
      };

      if (decision === 'approved') {
        updateData.role = 'instructor';
        updateData.approvedAt = new Date().toISOString();
      } else if (decision === 'rejected') {
        updateData.rejectedAt = new Date().toISOString();
      }

      await updateDoc(userRef, updateData);

      setApplications((prev) => prev.filter((app) => app.id !== userId));

      if (decision === 'approved') {
        const approvedUser = applications.find((app) => app.id === userId);
        if (approvedUser) {
          setInstructors((prev) => [...prev, { ...approvedUser, role: 'instructor' }]);
        }
      }

      setFlashMessage(
        decision === 'approved'
          ? 'Instructor application approved.'
          : 'Instructor application rejected.'
      );
    } catch (error) {
      console.error(`Error updating application for ${userId}:`, error);
    }
  };

  const handleOrgDraftChange = (userId, field, value) => {
    setOrgDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveOrgMembership = async (userId) => {
    const draft = orgDrafts[userId];
    if (!draft) return;

    try {
      const nextFields = {
        orgId: draft.orgId.trim(),
        orgName: draft.orgName.trim(),
        orgRole: draft.orgRole,
      };

      await updateDoc(doc(db, 'users', userId), nextFields);

      setApplications((prev) =>
        prev.map((user) =>
          user.id === userId
            ? { ...user, ...nextFields }
            : user
        )
      );

      setInstructors((prev) =>
        prev.map((user) =>
          user.id === userId
            ? { ...user, ...nextFields }
            : user
        )
      );

      setFlashMessage('Organization membership updated.');
    } catch (error) {
      console.error(`Error updating org membership for ${userId}:`, error);
    }
  };

  const handleAssignmentChange = (videoId, instructorId) => {
    setSelectedAssignments((prev) => ({
      ...prev,
      [videoId]: instructorId,
    }));
  };

  const handleAssignVideo = async (videoId) => {
    const instructorId = selectedAssignments[videoId];
    if (!instructorId) return;

    try {
      await updateDoc(doc(db, 'videos', videoId), {
        assignedInstructorId: instructorId,
        assignedTo: instructorId,
        assigned: true,
      });

      setVideos((prev) =>
        prev.map((video) =>
          video.id === videoId
            ? {
                ...video,
                assignedInstructorId: instructorId,
                assignedTo: instructorId,
                assigned: true,
              }
            : video
        )
      );

      const instructorName =
        instructors.find((instructor) => instructor.id === instructorId)?.name || 'Instructor';

      setFlashMessage(`Video assigned to ${instructorName}.`);
    } catch (error) {
      console.error(`Error assigning video ${videoId}:`, error);
    }
  };

  const handleReleaseVideo = async (videoId) => {
    try {
      await updateDoc(doc(db, 'videos', videoId), {
        assignedInstructorId: null,
        assignedTo: null,
        assigned: false,
      });

      setVideos((prev) =>
        prev.map((video) =>
          video.id === videoId
            ? {
                ...video,
                assignedInstructorId: null,
                assignedTo: null,
                assigned: false,
              }
            : video
        )
      );

      setSelectedAssignments((prev) => ({
        ...prev,
        [videoId]: '',
      }));

      setFlashMessage('Video released back to the claim queue.');
    } catch (error) {
      console.error(`Error releasing video ${videoId}:`, error);
    }
  };

  const renderVideoAssignmentCard = (video, { showRelease = false } = {}) => {
    const assignedInstructor = instructors.find((instructor) => instructor.id === video.assignedTo);

    return (
      <li key={video.id} className="border p-4 rounded shadow bg-white">
        <p><strong>Title:</strong> {getVideoTitle(video)}</p>
        <p><strong>Sport:</strong> {video.sport || 'Unknown'}</p>
        <p><strong>Student:</strong> {video.studentId || video.userId || 'Unknown'}</p>
        <p><strong>Status:</strong> {video.status}</p>
        {assignedInstructor && (
          <p><strong>Assigned Instructor:</strong> {assignedInstructor.name || assignedInstructor.email}</p>
        )}

        <div className="mt-3 flex flex-col md:flex-row gap-2">
          <select
            value={selectedAssignments[video.id] || ''}
            onChange={(e) => handleAssignmentChange(video.id, e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">Select instructor</option>
            {instructors.map((instructor) => (
              <option key={instructor.id} value={instructor.id}>
                {instructor.name || instructor.email || instructor.id}
              </option>
            ))}
          </select>

          <button
            onClick={() => handleAssignVideo(video.id)}
            disabled={!selectedAssignments[video.id]}
            className="bg-blue-600 text-white px-3 py-2 rounded disabled:opacity-50"
          >
            {showRelease ? 'Reassign Video' : 'Assign Video'}
          </button>

          {showRelease && (
            <button
              onClick={() => handleReleaseVideo(video.id)}
              className="bg-gray-200 text-gray-900 px-3 py-2 rounded"
            >
              Release to Claim Queue
            </button>
          )}
        </div>
      </li>
    );
  };

  const renderOrgMembershipEditor = (user) => (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
      <input
        type="text"
        value={orgDrafts[user.id]?.orgId || ''}
        onChange={(e) => handleOrgDraftChange(user.id, 'orgId', e.target.value)}
        placeholder="orgId"
        className="border rounded px-3 py-2 text-sm"
      />
      <input
        type="text"
        value={orgDrafts[user.id]?.orgName || ''}
        onChange={(e) => handleOrgDraftChange(user.id, 'orgName', e.target.value)}
        placeholder="orgName"
        className="border rounded px-3 py-2 text-sm"
      />
      <select
        value={orgDrafts[user.id]?.orgRole || 'instructor'}
        onChange={(e) => handleOrgDraftChange(user.id, 'orgRole', e.target.value)}
        className="border rounded px-3 py-2 text-sm"
      >
        <option value="instructor">Instructor</option>
        <option value="member">Member</option>
        <option value="admin">Admin</option>
      </select>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-10">
      <div>
        <h1 className="text-2xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-sm text-gray-600">
          Approve instructors, monitor real-coach uploads, and pre-assign videos when needed.
        </p>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Platform</p>
              <h2 className="mt-2 text-lg font-semibold text-slate-950">Content Pack Management</h2>
              <p className="mt-1 text-sm text-slate-600">
                Open the platform content pack workspace to manage premium packs and organization entitlements.
              </p>
            </div>
            <button
              onClick={() => navigate('/platform/content-packs')}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Open Content Packs
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Platform</p>
              <h2 className="mt-2 text-lg font-semibold text-slate-950">Billing Foundations</h2>
              <p className="mt-1 text-sm text-slate-600">
                Manage subscription plans, organization plan status, trials, and commercial access scaffolding.
              </p>
            </div>
            <button
              onClick={() => navigate('/platform/billing')}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Open Billing
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Organizations</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{platformMetrics.organizationCount}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lessons</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{platformMetrics.lessonCount}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Videos</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{platformMetrics.videoCount}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Completed Reviews</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{platformMetrics.completedReviewCount}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Drills</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{platformMetrics.drillCount}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Premium Drills</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{platformMetrics.premiumDrillCount}</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
          <div className="rounded-2xl bg-indigo-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Active Orgs</p>
            <p className="mt-2 text-2xl font-bold text-indigo-950">{platformAnalytics.activeOrganizationCount}</p>
          </div>
          <div className="rounded-2xl bg-sky-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-500">Trials</p>
            <p className="mt-2 text-2xl font-bold text-sky-950">{platformAnalytics.trialOrganizationCount}</p>
          </div>
          <div className="rounded-2xl bg-emerald-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500">Active Subs</p>
            <p className="mt-2 text-2xl font-bold text-emerald-950">{platformAnalytics.activeSubscriptionCount}</p>
          </div>
          <div className="rounded-2xl bg-amber-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-500">Pending Invites</p>
            <p className="mt-2 text-2xl font-bold text-amber-950">{platformAnalytics.pendingInviteCount}</p>
          </div>
          <div className="rounded-2xl bg-violet-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">Premium Orgs</p>
            <p className="mt-2 text-2xl font-bold text-violet-950">{platformAnalytics.premiumUsage.organizationsWithPremiumAccess}</p>
          </div>
          <div className="rounded-2xl bg-rose-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-500">Students</p>
            <p className="mt-2 text-2xl font-bold text-rose-950">{platformAnalytics.studentCount}</p>
          </div>
        </div>
        {actionMessage && (
          <div className="mt-4 rounded bg-green-50 px-4 py-3 text-sm text-green-700">
            {actionMessage}
          </div>
        )}
      </div>

      {loading ? (
        <p>Loading admin tools...</p>
      ) : (
        <>
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Platform Analytics</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div className="rounded border bg-white p-4 shadow">
                <p className="text-sm font-semibold text-slate-900">Plan Distribution</p>
                <div className="mt-2 space-y-1 text-sm text-slate-700">
                  {Object.entries(platformAnalytics.planDistribution).length ? (
                    Object.entries(platformAnalytics.planDistribution).map(([planName, count]) => (
                      <div key={planName}>{planName}: {count}</div>
                    ))
                  ) : (
                    <div>No active plan assignments yet.</div>
                  )}
                </div>
              </div>
              <div className="rounded border bg-white p-4 shadow">
                <p className="text-sm font-semibold text-slate-900">Entitlement Sources</p>
                <div className="mt-2 space-y-1 text-sm text-slate-700">
                  {Object.entries(platformAnalytics.entitlementSourceBreakdown).length ? (
                    Object.entries(platformAnalytics.entitlementSourceBreakdown).map(([sourceType, count]) => (
                      <div key={sourceType}>{sourceType}: {count}</div>
                    ))
                  ) : (
                    <div>No premium entitlements yet.</div>
                  )}
                </div>
              </div>
              <div className="rounded border bg-white p-4 shadow">
                <p className="text-sm font-semibold text-slate-900">Premium Usage</p>
                <div className="mt-2 space-y-1 text-sm text-slate-700">
                  <div>Premium packs: {platformAnalytics.premiumUsage.premiumPackCount}</div>
                  <div>Premium drills: {platformAnalytics.premiumUsage.premiumTemplateCount}</div>
                  <div>Plan codes using premium: {platformAnalytics.premiumUsage.activePlanCodesWithPremiumAccess}</div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Pending Instructor Applications</h2>
            {applications.length === 0 ? (
              <p>No pending applications at the moment.</p>
            ) : (
              <ul className="space-y-4">
                {applications.map((app) => (
                  <li key={app.id} className="border p-4 rounded shadow bg-white">
                    <p><strong>Name:</strong> {app.name}</p>
                    <p><strong>Email:</strong> {app.email}</p>
                    <p><strong>Sport:</strong> {app.instructorApplication?.sports}</p>
                    <p><strong>Experience:</strong> {app.instructorApplication?.experience}</p>
                    <p><strong>Bio:</strong> {app.instructorApplication?.bio}</p>

                    {renderOrgMembershipEditor(app)}

                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleSaveOrgMembership(app.id)}
                        className="bg-slate-200 text-slate-900 px-3 py-1 rounded"
                      >
                        Save Org
                      </button>
                      <button
                        onClick={() => handleDecision(app.id, 'approved')}
                        className="bg-green-600 text-white px-3 py-1 rounded"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDecision(app.id, 'rejected')}
                        className="bg-red-600 text-white px-3 py-1 rounded"
                      >
                        Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Available Instructors</h2>
            {instructorWorkload.length === 0 ? (
              <p>No approved instructors available yet.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[220px]">
                    <label className="mb-1 block text-sm font-medium text-slate-700">Filter By Org</label>
                    <select
                      value={orgFilter}
                      onChange={(e) => setOrgFilter(e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm"
                    >
                      <option value="all">All organizations</option>
                      {orgBuckets.map((bucket) => (
                        <option key={bucket.orgId} value={bucket.orgId}>
                          {bucket.orgName} ({bucket.instructorCount})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {orgBuckets.map((bucket) => (
                    <div key={bucket.orgId} className="rounded border bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-900">{bucket.orgName}</p>
                      <p className="mt-2 text-sm text-slate-700">Instructors: {bucket.instructorCount}</p>
                      <p className="text-sm text-slate-700">Assigned reviews: {bucket.assignedCount}</p>
                      <p className="text-sm text-slate-700">Completed reviews: {bucket.reviewedCount}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-6">
                  {filteredOrgBuckets.map((bucket) => (
                    <div key={bucket.orgId} className="space-y-3">
                      <div>
                        <h3 className="text-lg font-semibold">{bucket.orgName}</h3>
                        <p className="text-sm text-gray-600">
                          {bucket.instructorCount} instructor{bucket.instructorCount === 1 ? '' : 's'} in this org group.
                        </p>
                      </div>

                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {bucket.instructors.map((instructor) => (
                          <li key={instructor.id} className="border p-4 rounded shadow bg-white">
                            <p><strong>Name:</strong> {instructor.name || 'Unnamed Instructor'}</p>
                            <p><strong>Email:</strong> {instructor.email || 'No email available'}</p>
                            <p><strong>Application Status:</strong> {instructor.applicationStatus || 'approved'}</p>
                            <p><strong>Org:</strong> {instructor.orgName || instructor.orgId || 'Unassigned'}</p>
                            <p><strong>Org Role:</strong> {instructor.orgRole || 'instructor'}</p>
                            <p><strong>Assigned Right Now:</strong> {instructor.assignedCount}</p>
                            <p><strong>Completed Reviews:</strong> {instructor.reviewedCount}</p>

                            {renderOrgMembershipEditor(instructor)}

                            <button
                              onClick={() => handleSaveOrgMembership(instructor.id)}
                              className="mt-3 bg-slate-800 text-white px-3 py-2 rounded"
                            >
                              Save Org Membership
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Videos Awaiting Assignment</h2>
            <p className="text-sm text-gray-600">
              These are real-instructor uploads that are still in the open claim queue.
            </p>
            {pendingAssignmentVideos.length === 0 ? (
              <p>No real-coach videos are waiting for assignment right now.</p>
            ) : (
              <ul className="space-y-4">
                {pendingAssignmentVideos.map((video) => renderVideoAssignmentCard(video))}
              </ul>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Assigned Videos</h2>
            <p className="text-sm text-gray-600">
              Reassign a video if you want to move it away from the normal instructor claim flow.
            </p>
            {assignedVideos.length === 0 ? (
              <p>No videos are actively assigned right now.</p>
            ) : (
              <ul className="space-y-4">
                {assignedVideos.map((video) => renderVideoAssignmentCard(video, { showRelease: true }))}
              </ul>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold">Reviewed Videos</h2>
            {reviewedVideos.length === 0 ? (
              <p>No reviewed real-coach videos yet.</p>
            ) : (
              <ul className="space-y-4">
                {reviewedVideos.map((video) => (
                  <li key={video.id} className="border p-4 rounded shadow bg-white">
                    <p><strong>Title:</strong> {video.videoName || 'Untitled Video'}</p>
                    <p><strong>Sport:</strong> {video.sport || 'Unknown'}</p>
                    <p><strong>Status:</strong> {video.status}</p>
                    <p>
                      <strong>Reviewed By:</strong>{' '}
                      {instructors.find((instructor) => instructor.id === video.reviewedBy)?.name ||
                        video.reviewedBy ||
                        'Unknown'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default AdminDashboard;
